<script>
    import {required} from 'vuelidate/lib/validators';
    import {LANGUAGE_MAPPING} from '@/services/Constants';

    export default {
        name: 'EmailConfiguration',
        props: {
            isSubmitted: Boolean,
            eMailSubject: String,
            eMailBody: String,
            placeholders: Array,
            required: true,
            language: String,
        },
        data() {
            return {
                languageTabs: ['Deutsch', 'Englisch', 'Französisch', 'Benutzerdefiniert'],
                checkedPlaceholders: {},
            };
        },
        computed: {
            currentLanguage: {
                get() {
                    return LANGUAGE_MAPPING[this.language];
                },
                set(language) {
                    this.$emit('update', {language: LANGUAGE_MAPPING[language]});
                },
            },
            body: {
                get() {
                    return this.placeholdersToTags(this.placeholderValueToName(this.eMailBody));
                },
                set(body) {
                    this.$emit('update', {eMailBody: this.placeholderNameToValue(this.$sanitize(this.tagsToPlaceholders(body)))});
                },
            },
            subject: {
                get() {
                    return this.eMailSubject;
                },
                set(subject) {
                    this.$emit('update', {eMailSubject: subject});
                },
            },
            placeholdersNames() {
                return this.placeholders.map(placeholder => placeholder.name);
            },
        },
        validations() {
            if (this.currentLanguage === 'Benutzerdefiniert') {
                return {
                    subject: {
                        required,
                    },
                    body: {
                        required,
                    },
                };
            } else {
                return {
                    subject: {
                        required,
                    },
                };
            }
        },
        methods: {
            handlePlaceholder(event, name) {
                const styledPlaceholder = '<span class="text-placeholder" contenteditable="false">' + name + '</span>';
                if (event.target.checked) {
                    this.body += styledPlaceholder + '&nbsp;';
                } else {
                    this.body = this.body.replace(styledPlaceholder, '');
                }
            },
            placeholdersToTags(bodyText) {
                return this.crToBreak(bodyText).replace(/\%([a-zA-Z_0-9_äöüÄÖÜùûüÿàâæéèêëïîôœÙÛÜŸÀÂÆÉÈÊËÏÎÔŒ\’ ]+)\%/g, this.placeholderReplacer);
            },
            placeholderReplacer(match, p1) {
                if (this.placeholdersNames.indexOf(p1) === -1) {
                    return '';
                }
                const placeholderClass = this.currentLanguage === this.languageTabs[3] ? 'text-placeholder' : 'text-placeholder-inactive';
                return '<span class="' + placeholderClass + '" contenteditable="false">' + p1 + '</span>';
            },
            tagsToPlaceholders(bodyText) {
                return this.breakToCr(bodyText).replace(/\<span class\=\"text-placeholder\" contenteditable\=\"false\"\>/g, '%')
                    .replace(/\<\/span\>/g, '%');
            },
            crToBreak(text) {
                return text.replace(/(?:\r\n|\r|\n)/g, '<br>');
            },
            breakToCr(text) {
                return text.replace(/\<br\>/g, '\n');
            },
            placeholderNameToValue(bodyText) {
                return this.placeholders.reduce((text, placeholder) => {
                    return text.replace('%' + placeholder.name + '%', placeholder.placeholder);
                }, bodyText);
            },
            placeholderValueToName(bodyText) {
                this.checkedPlaceholders = {};
                return this.placeholders.reduce((text, placeholder) => {
                    if (text.indexOf(placeholder.placeholder) !== -1) {
                        this.checkedPlaceholders[placeholder.name] = true;
                    }
                    return text.replace(placeholder.placeholder, '%' + placeholder.name + '%');
                }, bodyText);
            },
            replaceDivs(html) {
              return html.replace(/\<div\>\<br\>/g, '\n').replace(/\<div\>/g, '\n').replace(/\<\/div\>/g, '');
            },
            onBodyInput(event) {
                const innerHTML = this.replaceDivs(event.target.innerHTML);
                const withPlaceholders = this.tagsToPlaceholders(innerHTML);
                const sanitized = String(this.$sanitize(withPlaceholders));
                this.body = this.placeholdersToTags(sanitized);
            },
        },
    };
</script>

<template>
    <div class="email-configuration">
        <h2>E-Mail-Konfiguration</h2>
        <p>Stelle in diesem Bereich ein, wie der Inhalt der E-Mail aussehen soll, wenn Konferenzteilnehmer eine
            Einladung zu
            einer Konferenz erhalten.</p>
        <p>Du kannst wählen zwischen unseren drei vorgefertigten Texten in Deutsch, Englisch und Französisch oder Du
            schreibst einen komplett eigenen Text, indem Du „Benutzerdefiniert“ wählst.</p>
        <form class="form">
            <div class="form-group form-text">
                <label for="eMailSubject" class="eMailSubject">Betreff der E-Mail:</label>
                <input type="text" name="eMailSubject" id="eMailSubject" class="form-control"
                       :class="{'has-error': isSubmitted && $v.subject.$invalid}" placeholder="Konferenz"
                       v-model="subject" required/>
                <span class="error-message"
                      v-if="isSubmitted && $v.subject.$invalid">Bitte gebe einen Betreff für die E-Mail ein.</span>
            </div>

            <div class="lang-config" v-if="currentLanguage">
                <template v-for="languageTab in languageTabs">
                    <div :class="['tab', { active: currentLanguage === languageTab }]" :key="languageTab">
                        <input type="radio"
                               :id="languageTab"
                               :value="languageTab"
                               v-model="currentLanguage"/>
                        <label :for="languageTab">{{ languageTab }}</label>
                    </div>
                </template>
            </div>
            <div class="body-config" v-if="currentLanguage">
                <template v-if="currentLanguage === languageTabs[3]">
                    <span class="placeholder-menu-title">Per Klick in den Text einfügen:</span>
                    <div class="placeholder-menu-list">
                        <template v-for="placeholder in placeholders">
                            <div class="placeholder-menu-list-item" :key="placeholder.name">
                                <input type="checkbox" :id="placeholder.name" :value="placeholder.name"
                                       @change="handlePlaceholder($event, placeholder.name)"
                                       v-model="checkedPlaceholders[placeholder.name]">
                                <label :for="placeholder.name">
                                    {{ placeholder.name}}
                                    <img v-if="!checkedPlaceholders[placeholder.name]"
                                         src="@/assets/starface_icons_png/16/grey/checkmark-inactive_16@2x.png"
                                         alt="Check"/>
                                    <img v-if="checkedPlaceholders[placeholder.name]"
                                         src="@/assets/starface_icons_png/16/orange/checkmark-active_16@2x.png"
                                         alt="Check"/>
                                </label>
                            </div>
                        </template>
                    </div>
                    <div class="body-text" contentEditable="true" v-html="body"
                         :class="{'has-error': isSubmitted && $v.body.$invalid}"
                         @blur="onBodyInput($event)">
                    </div>
                    <span class="error-message" v-if="isSubmitted && $v.body.$invalid">Bitte gebe einen Text ein.</span>
                </template>

                <div v-if="currentLanguage !== languageTabs[3]" class="body-text-static" v-html="body">
                </div>
            </div>
        </form>
    </div>
</template>

<style lang="scss">
    @import '../../assets/scss/variables.scss';

    .text-placeholder, .text-placeholder-inactive {
        user-select: none;
        -webkit-user-drag: element;
        display: inline-block;
        border-radius: 2px;
        font-size: 12px;
        line-height: 12px;
        height: 22px;
        padding: 5px;
        text-transform: uppercase;
    }

    .text-placeholder {
        border-top: 2px solid $orange;
        background-color: #fff;
        color: $grey;
        box-shadow: 0 10px 45px 0 rgba(0, 0, 0, 0.1);
    }

    .text-placeholder-inactive {
        background-color: $grey-middle;
        color: white;
        border-top: 0;
    }
</style>

<style lang="scss" scoped>
    @import '../../assets/scss/variables.scss';
    @import '../../assets/scss/general.scss';
    @import '../../assets/scss/forms.scss';
    @import '../../assets/scss/inputs.scss';

    .email-configuration {
        margin-top: 30px;
    }

    .eMailSubject {
        color: $grey-middle;
        font-family: 'Open Sans', Arial, sans-serif;
        font-size: 14px;
        line-height: 19px;
    }

    .form .form-group {
        margin-bottom: 30px;
    }

    .lang-config {
        display: flex;
        justify-content: stretch;
        .tab {
            height: 95px;
            flex: 1 auto;
            flex-basis: 25%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-top: 3px solid $grey;
        }

        .active {
            border-top: 3px solid $orange;
            background-color: $grey-lighter;
        }

        input[type=radio] + label {
            overflow: visible;
            padding: 0;
            margin-bottom: 25px;
            &:after {
                left: calc(50% - #{$radio-width / 2});
                top: 30px;

            }
        }
    }

    .body-config {
        background-color: $grey-lighter;
        padding: 30px;

        .placeholder-menu-title {
            display: block;
        }

        .placeholder-menu-list {
            margin-top: 15px;
        }

        .placeholder-menu-list-item {
            display: inline-block;
        }

        input[type=checkbox] + label {
            display: inline-block;
            background-color: #fff;
            border-radius: 2px;
            padding: 0 5px;
            border-top: 2px solid $grey;
            font-size: 12px;
            line-height: 12px;
            height: 22px;
            color: $grey;
            margin-right: 15px;
            box-shadow: 0 10px 45px 0 rgba(0, 0, 0, 0.1);
            text-transform: uppercase;

            &:after, &:before {
                display: none;
            }

            img {
                width: 14px;
                top: 3px;
                position: relative;
            }
        }

        input[type=checkbox]:checked + label {
            font-weight: normal;
            border-top: 2px solid $orange;
        }

        .body-text, .body-text-static {
            color: $grey-dark;
            font-family: 'Open Sans', Arial, sans-serif;
            font-size: 18px;
            width: 100%;
        }

        .body-text {
            padding: 10px;
            margin-top: 15px;
            min-height: 221px;
            border-radius: 4px;
            background-color: #FFFFFF;
            box-shadow: inset 0 0 10px 0 rgba(0, 0, 0, 0.2);
        }
    }

    .error-message {
        font-size: 12px;
        font-weight: 600;
        text-align: left;
        color: $red;
    }

    .has-error {
        border: 1px solid $red;
    }
</style>